import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import { NAV_LINKS, SIGNUP_URL, LOGIN_URL } from "../../data/content";

const NavItem = ({ href, className, onClick, children, testId }) => {
  const isRoute = href.startsWith("/") && !href.startsWith("/#");
  if (isRoute) {
    return (
      <Link to={href} className={className} onClick={onClick} data-testid={testId}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick} data-testid={testId}>
      {children}
    </a>
  );
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-container"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#E6E9F1] shadow-[0_4px_20px_rgba(11,21,51,0.05)]"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img
            src="/logos/magicbolt.webp"
            alt="MagicBolt AI"
            width="706"
            height="133"
            className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavItem
              key={l.label}
              href={l.href}
              testId="nav-link"
              className={`relative text-sm text-[#475569] hover:text-[#0B1533] transition-colors after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-0 after:bg-[#FF6B00] after:transition-[width] after:duration-200 hover:after:w-full ${
                l.bold ? "font-bold" : "font-medium"
              }`}
            >
              {l.label}
            </NavItem>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <a
            href={LOGIN_URL}
            data-testid="nav-login-link"
            className="text-sm font-medium text-[#475569] hover:text-[#0B1533] transition-colors"
          >
            Log in
          </a>
          <Button
            data-testid="nav-cta-button"
            className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(255,107,0,0.35)]"
            asChild
          >
            <a href={SIGNUP_URL}>Start Free Trial</a>
          </Button>
        </div>

        <button
          className="md:hidden text-[#0B1533] relative grid place-items-center w-8 h-8"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span
                key="close"
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                className="absolute inset-0 grid place-items-center"
              >
                <X className="w-6 h-6" />
              </motion.span>
            ) : (
              <motion.span
                key="menu"
                initial={{ opacity: 0, rotate: 90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: -90 }}
                transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                className="absolute inset-0 grid place-items-center"
              >
                <Menu className="w-6 h-6" />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-[#E6E9F1]"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <NavItem
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className={`text-base text-[#475569] hover:text-[#0B1533] ${l.bold ? "font-bold" : "font-medium"}`}
                >
                  {l.label}
                </NavItem>
              ))}
              <a
                href={LOGIN_URL}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[#475569] hover:text-[#0B1533]"
              >
                Log in
              </a>
              <Button className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold" asChild>
                <a href={SIGNUP_URL} onClick={() => setOpen(false)}>Start Free Trial</a>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
